import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { fetchJiraFieldsQuery } from '@atlassian/search-client/hydration';
import { serveFpsMultiSiteSearchExperiment } from '@atlassian/search-common/experiments';

import type { PrimaryProductKey } from '../../../common/constants/product-keys';
import type { JiraIssueStatusAndColorName } from './search-result/search-result-renderer/search-jira-issue';

export const fetchAndUpdateJiraFields = async (
	cloudId: string | undefined,
	issueKeys: string[],
	aggAbsoluteUrl: string | undefined,
	setJiraStatuses: React.Dispatch<
		React.SetStateAction<Record<string, JiraIssueStatusAndColorName | null>>
	>,
	logError: (error: unknown) => void,
	activationId?: string,
	product?: PrimaryProductKey,
	issueKeysByCloudId?: ReadonlyMap<string, readonly string[]>,
): Promise<void> => {
	if (cloudId && issueKeys.length > 0) {
		try {
			const requests =
				serveFpsMultiSiteSearchExperiment({
					primaryProduct: product,
					fireExperimentExposure: false,
				}) !== 'control' && issueKeysByCloudId?.size
					? Array.from(issueKeysByCloudId, ([siteCloudId, siteIssueKeys]) => ({
							cloudId: siteCloudId,
							issueKeys: siteIssueKeys,
						}))
					: [{ cloudId, issueKeys }];
			const responses = await Promise.all(
				requests.map(({ cloudId: siteCloudId, issueKeys: siteIssueKeys }) => {
					const headers = buildAtlAttributionHeaders({
						cloudId: siteCloudId,
						activationId,
						product,
					});

					return fetchJiraFieldsQuery({
						variables: {
							issueKeys: [...siteIssueKeys],
							cloudId: siteCloudId,
						},
						aggAbsoluteUrl,
						headers,
					});
				}),
			);
			const issuesByKey = responses.flatMap((response) => response.data?.jira?.issuesByKey ?? []);
			setJiraStatuses((prevIssueStatuses) => {
				if (issuesByKey.length > 0) {
					const issueStatuses: Record<string, JiraIssueStatusAndColorName | null> = {
						...prevIssueStatuses,
					};
					// Have seen the issuesByKey have [null] responses
					for (const issue of issuesByKey.filter((issue) => issue)) {
						const statusName = issue.statusField?.status?.name;
						const colorName = issue.statusField?.status?.statusCategory?.colorName;
						const statusCategoryId = issue.statusField?.status?.statusCategory?.statusCategoryId;
						if (issue.id && statusName && colorName) {
							issueStatuses[issue.id] = {
								statusName,
								colorName,
								statusCategoryId,
							};
						}
					}
					return issueStatuses;
				}
				return prevIssueStatuses;
			});
			return;
		} catch (e) {
			logError(e);
		}
	}
	// If fetching Jira fields fails, we still want to update the issue statuses to null
	setJiraStatuses((prevIssueStatuses) => {
		const issueStatuses: Record<string, JiraIssueStatusAndColorName | null> = {
			...prevIssueStatuses,
		};
		for (const key of issueKeys) {
			issueStatuses[key] = null;
		}
		return issueStatuses;
	});
};
